import { cn } from '@/shared/lib/cn';
import { Badge } from '@/shared/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import type { AuditEventType, AuditTrailItem } from '@skewb-works/contracts';
import { History } from 'lucide-react';
import {
  describeAuditEvent,
  formatEntityType,
  formatEventType,
  formatTimestamp,
} from '../utils/auditTrailFormatters';

const EVENT_TYPE_COLORS: Record<AuditEventType, string> = {
  status_change: 'bg-blue-100 text-blue-800 border-blue-300',
  assignment: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  completion: 'bg-green-100 text-green-800 border-green-300',
  authority_override: 'bg-red-100 text-red-800 border-red-300',
  field_update: 'bg-amber-100 text-amber-800 border-amber-300',
  creation: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  deletion: 'bg-red-100 text-red-800 border-red-300',
};

interface AuditTrailTableProps {
  items: AuditTrailItem[];
}

export function AuditTrailTable({ items }: AuditTrailTableProps) {
  if (items.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <History className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Audit Trail Entries</h3>
          <p className="text-sm text-muted-foreground">
            No entries found for the selected filters and date range.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[160px]">Timestamp</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Event Type</TableHead>
            <TableHead>Entity</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Reason</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id}>
              <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                {formatTimestamp(item.created_at)}
              </TableCell>
              <TableCell className="font-medium">{item.username}</TableCell>
              <TableCell>
                <Badge
                  variant="outline"
                  className={cn(EVENT_TYPE_COLORS[item.event_type], 'text-nowrap')}
                >
                  {formatEventType(item.event_type)}
                </Badge>
              </TableCell>
              <TableCell className="text-sm">
                {formatEntityType(item.entity_type)} #{item.entity_id}
              </TableCell>
              <TableCell className="text-sm max-w-[400px] truncate">
                {describeAuditEvent(item)}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                {item.reason ?? '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
